import './CompensationHistoryGridResults.scss';
import groups from '../../constants/groups.json';
import { CompensationHistoryInterface, HistoryGridRowInterface } from '../../models/history';
import { formatDateInFrench } from '../../helpers/date';
import { GridColDef, GridAlignment } from '@mui/x-data-grid';
import { HISTORY_VIEW, LANGUAGE } from '../../constants/common';
import { NavigateFunction } from 'react-router-dom';
import { useCompensationHistoryGridResults } from './useCompensationHistoryGridResults';
import AppTable from '../AppCommon/AppTable/AppTable';
import EditIcon from '@mui/icons-material/Edit';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import SendIcon from '@mui/icons-material/Send';
import UpdateEmailPopUp from '../UpdateEmailPopUp/UpdateEmailPopUp';
import PreviewIcon from '@mui/icons-material/Preview';
import { Snackbar } from '@mui/material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { forwardRef } from 'react';
import { Field } from '../../models/home';
import CircularProgress from '@mui/material/CircularProgress';
import React from 'react';

interface Props {
    rows: CompensationHistoryInterface[];
    handleOnClickSearch: (pageNumber?: number, pageSize?: string) => void;
    navigate: NavigateFunction;
    historyView: string;
    showCompensationCodes?: (row: CompensationHistoryInterface) => void;
    searchFields?: Field[];
}

const CompensationHistoryGridResults = ({
    rows, 
    handleOnClickSearch, 
    navigate, 
    historyView,
    showCompensationCodes,
    searchFields
}: Props) => {
    const {
        historyContext,
        defaultResults,
        i18n,
        openUpdateEmailDialog,
        updateEmailPayload,
        editErroredVoucher,
        getRowClassName,
        getServerSidePaginationCount,
        getTranslatedReason,
        getReasonTitleById,
        getCategoryTitleById,
        getCabinTitleById,
        sendCompensationCodes,
        setOpenUpdateEmailDialog,
        setUpdateEmailPayload,
        saveUpdatedEmailAddress,
        t,
        updateEmailAddress,
        openSentConfirmation,
        handleCloseEmailUpdatedConfirmation,
        getDisplaySubType,
    } = useCompensationHistoryGridResults({
        rows, 
        navigate,
        searchFields,
        historyView
    });

    // Add local state for tracking which row is loading for resend
    const [resendLoadingRowId, setResendLoadingRowId] = React.useState<string | null>(null);
    // Add local state for tracking which row is loading for view code
    const [viewCodeLoadingRowId, setViewCodeLoadingRowId] = React.useState<string | null>(null);
    // Track the row for which resend was triggered
    const resendRowRef = React.useRef<any>(null);

    React.useEffect(() => {
        if (!historyContext.loading.voucherCode && viewCodeLoadingRowId) {
            setViewCodeLoadingRowId(null);
        }
    }, [historyContext.loading.voucherCode, viewCodeLoadingRowId]);

    React.useEffect(() => {
        if (resendLoadingRowId && historyContext.emailSent) {
            setResendLoadingRowId(null);
        }
    }, [historyContext.emailSent, resendLoadingRowId]);

    // Wrap sendCompensationCodes to set loading state
    const handleResend = React.useCallback((row: any) => {
        setResendLoadingRowId(row.id);
        resendRowRef.current = row;
        sendCompensationCodes(row);
    }, [sendCompensationCodes]);

    // Wrap showCompensationCodes to set loading state
    const handleViewCode = React.useCallback((row: any) => {
        setViewCodeLoadingRowId(row.id);
        showCompensationCodes && showCompensationCodes(row);
    }, [showCompensationCodes]);
    
    const historyColumnsIpad: GridColDef[] = [
        {
            field: 'customer',
            headerAlign: 'left' as GridAlignment,
            headerClassName: 'history-table-header',
            headerName: '',
            sortable: false,
            width: 500,
            renderCell: (params: HistoryGridRowInterface) => {
                const status = (params.row.status ?? params.row.voucherStatusCode) as string | undefined;
                let statusPill: React.ReactNode = null;
                const isPending = typeof status === 'string' && ['VALIDATING', 'QUEUED', 'PROCESSING'].includes(status.toUpperCase());
                if (typeof status === 'string') {
                    if (['PROCESSING', 'VALIDATING'].includes(status.toUpperCase())) {
                        statusPill = <span className="status-pill status-pill--sending">{t('historyPage.sending')}</span>;
                    } else if (status.toUpperCase() === 'QUEUED') {
                        statusPill = <span className="status-pill status-pill--queued">{t('historyPage.queued')}</span>;
                    }
                }
                return (
                    <div className='c-cell-customer' style={{position: 'relative'}}>
                        <div className='c-name c'>
                            <span>
                                <span className='bold mr-0-5'>{t('historyPage.name')}:</span>
                                {params.row?.customer}
                                {statusPill}
                            </span>
                            {historyView === HISTORY_VIEW.ifs && (
                                params.row.error || isPending ? (
                                    null
                                ) : (
                                    <div
                                    className='c-inner'
                                    onClick={() => params.row.type === 'WIFI' && handleViewCode(params.row)}
                                >
                                    {params.row.type === 'WIFI' ? (viewCodeLoadingRowId === params.row.id && historyContext.loading.voucherCode ? <CircularProgress size={20} /> : <PreviewIcon />) : null}
                                </div>
                                )
                            )   
                            }
                        </div>
                        <div className='c'>
                            <span className='c-inner'>
                                <span className='bold mr-0-5'>{t('historyPage.flight')}:</span>
                                {params.row?.flight}
                            </span>
                            <span className='c-inner'>
                                <span className='bold mr-0-5'>{t('historyPage.date')}:</span>
                                {i18n.language === LANGUAGE.en ? params.row?.date : formatDateInFrench(params.row?.date)}
                            </span>
                            {!params.row?.error && (
                                <span 
                                    className='c-inner c-icon'
                                    style={{
                                        visibility: 'visible',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => updateEmailAddress(params.row)}
                                >
                                    <span className='bold mr-0-5'>{t('historyPage.edit')}</span>
                                    <EditIcon />
                                </span>
                            )}
                            {params.row?.error && (
                                <span 
                                    className={`c-inner c-icon ${!params.row?.reasonId ? 'c-icon--disabled' : ''}`}
                                    style={{
                                        visibility: defaultResults ? 'hidden' : 'visible',
                                        opacity: !params.row?.reasonId ? 0.5 : 1,
                                        cursor: !params.row?.reasonId ? 'not-allowed' : 'pointer'
                                    }}
                                    onClick={() => {
                                        if (params.row?.reasonId) {
                                            editErroredVoucher(params.row);
                                        }
                                    }}
                                >
                                    <span className='bold mr-0-5'>{t('historyPage.edit')}</span>
                                    <EditIcon />
                                </span>
                            )}
                        </div>
                        <div className='c'>
                            <span className='c-inner'>
                                <span className='bold mr-0-5'>{t('historyPage.pnr')}:</span>
                                {params.row?.bookingReference}
                            </span>
                            <span 
                                className='c-inner c-icon' 
                                style={{visibility: defaultResults || params.row?.error || isPending ? 'hidden' : 'visible'}}
                            >
                                <span className='bold mr-0-5' onClick={() => handleResend(params.row)}>
                                    {resendLoadingRowId === params.row.id ? <CircularProgress size={20} /> : t('historyPage.send')}
                                </span>
                                {resendLoadingRowId === params.row.id ? null : <SendIcon />}
                            </span>
                        </div>
                        <div className='c'>
                            <span className='c-inner'>
                                <span className='bold mr-0-5'>{t('historyPage.email')}:</span>
                                {params.row?.email}
                            </span>
                        </div>
                        {params.row?.error ? (
                            <div className='c-error'>
                                <ReportGmailerrorredIcon />
                                <span>{params.row?.errorMessage}</span>
                            </div>
                        )   : null}
                        <div className={`c-ipad ${defaultResults ? '' : 'c-details-show'}`}>
                            <div className='c-info'>
                                <span className='c-text'><span className='bold mr-0-5'>{t('historyPage.reason')}:</span>{getReasonTitleById(params.row?.reasonId) || getTranslatedReason(params.row?.reason) || params.row?.reason}</span>
                                {(() => {
                                    const title = getCategoryTitleById(params.row?.categoryId);
                                    return title ? (
                                        <span className='c-text'><span className='bold mr-0-5'>{t('labels.compensationCategory') || 'Category'}:</span>{title}</span>
                                    ) : null;
                                })()}
                                {(() => {
                                    const title = getCabinTitleById(params.row?.cabinId, params.row?.categoryId);
                                    return title ? (
                                        <span className='c-text'><span className='bold mr-0-5'>{t('labels.compensationCabin') || 'Cabin'}:</span>{title}</span>
                                    ) : null;
                                })()}
                                <span className='c-text'><span className='bold mr-0-5'>{t('historyPage.type')}:</span>{defaultResults ? '' : (params.row?.type === "NTP" ? getDisplaySubType(params.row?.subType) : t('compensationTypes.wifi'))}</span>
                                <span className='c-text'><span className='bold mr-0-5'>{t('historyPage.qty')}:</span>{params.row?.qty}</span>
                            </div>
                            <div className='mt-2'>
                                <span className='bold mr-0-5'>{t('historyPage.comments')}:</span>{params.row?.comments !== "Optional" && params.row?.comments}
                                {(() => {
                                    const normalized = (params.row?.groupId || '').toString().trim();
                                    if (!normalized) return null;
                                    const match = (groups as any[]).find(g => (g.groupId || '').toString().trim().toLowerCase() === normalized.toLowerCase());
                                    return (
                                        <span className='c-issued-by'>
                                            <span className='bold mr-0-5'>{t('historyPage.issuedBy')}:</span>
                                            {match ? t(`groupNames.${(match as any).key}`) : `${t('historyPage.unknown')} (${params.row?.groupId})`}
                                        </span>
                                    );
                                })()}
                            </div>                  
                        </div>
                    </div>
                );
            }
        }
    ];
    
    const historyColumnsWeb: GridColDef[] = [
        {   
            field: 'customer',
            headerAlign: 'left' as GridAlignment, 
            headerClassName: 'col-header-web',
            headerName: t('historyPage.customer'),
            sortable: false,
            width: 220,
            renderCell: (params: HistoryGridRowInterface) => {
                const status = (params.row.status ?? params.row.voucherStatusCode) as string | undefined;
                let statusPill: React.ReactNode = null;
                if (typeof status === 'string') {
                    if (['PROCESSING', 'VALIDATING'].includes(status.toUpperCase())) {
                        statusPill = <span className="status-pill status-pill--sending">{t('historyPage.sending')}</span>;
                    } else if (status.toUpperCase() === 'QUEUED') {
                        statusPill = <span className="status-pill status-pill--queued">{t('historyPage.queued')}</span>;
                    }
                }
                return (
                    <div className='c-cell-customer' style={{position: 'relative'}}>
                        <div className='c'>
                            {params.row?.customer}
                            {statusPill}
                        </div>
                        {params.row?.error ? (
                            <div className='c-error'>
                                <ReportGmailerrorredIcon />
                                <span>{params.row?.errorMessage}</span>
                            </div>
                        )  : null}
                        <div className={`c-web ${defaultResults ? '' : 'c-details-show'}`}>
                            <div className='c-info'>
                                <span className='c-text'><span className='bold mr-0-5'>{t('historyPage.reason')}:</span>{getReasonTitleById(params.row?.reasonId) || getTranslatedReason(params.row?.reason) || params.row?.reason}</span>
                                {(() => {
                                    const title = getCategoryTitleById(params.row?.categoryId);
                                    return title ? (
                                        <span className='c-text'><span className='bold mr-0-5'>{t('labels.compensationCategory') || 'Category'}:</span>{title}</span>
                                    ) : null;
                                })()}
                                {(() => {
                                    const title = getCabinTitleById(params.row?.cabinId, params.row?.categoryId);
                                    return title ? (
                                        <span className='c-text'><span className='bold mr-0-5'>{t('labels.compensationCabin') || 'Cabin'}:</span>{title}</span>
                                    ) : null;
                                })()}
                                <span className='c-text'><span className='bold mr-0-5'>{t('historyPage.qty')}:</span>{params.row?.qty}</span>
                            </div>
                            <div className='mt-2'>
                                <span className='bold mr-0-5'>{t('historyPage.comments')}:</span>{params.row?.comments !== "Optional" && params.row?.comments}
                                {(() => {
                                    const normalized = (params.row?.groupId || '').toString().trim();
                                    if (!normalized) return null;
                                    const match = (groups as any[]).find(g => (g.groupId || '').toString().trim().toLowerCase() === normalized.toLowerCase());
                                    return (
                                        <span className='c-issued-by'>
                                            <span className='bold mr-0-5'>{t('historyPage.issuedBy')}:</span>
                                            {match ? t(`groupNames.${(match as any).key}`) : `${t('historyPage.unknown')} (${params.row?.groupId})`}
                                        </span>
                                    );
                                })()}
                            </div>                   
                        </div>
                    </div>
                );
            },
        },
        {
            field: 'flight',
            headerName: t('historyPage.flight'),
            width: 100,
            headerAlign: 'left' as GridAlignment,
            headerClassName: 'col-header-web',
            sortable: false,
            renderCell: (params) => (
                <div className='c-cell c-cell--flight'>{params.row?.flight}</div>
            )
        },
        {
            field: 'date',
            headerAlign: 'left' as GridAlignment,
            headerClassName: 'col-header-web',
            headerName: t('historyPage.date'),
            sortable: false,
            width: 160,
            renderCell: (params) => (
                <div className='c-cell c-cell--date'>{i18n.language === LANGUAGE.en ? params.row?.date : formatDateInFrench(params.row?.date)}</div>
            )
        },
        {
            field: 'pnr',
            headerAlign: 'left' as GridAlignment,
            headerClassName: 'col-header-web',
            headerName: t('historyPage.pnr'),
            sortable: false,
            width: 140,
            renderCell: (params) => (
                <div className='c-cell c-cell--pnr'>{params.row?.bookingReference}</div>
            )
        },
        {
            field: 'email',
            headerAlign: 'left' as GridAlignment,
            headerClassName: 'col-header-web',
            headerName: t('historyPage.email'),
            sortable: false,
            width: 290,
            renderCell: (params) => (
                <div className='c-cell'>{params.row?.email}</div>
            )
        },
        {
            field: 'type',
            headerAlign: 'left' as GridAlignment,
            headerClassName: 'col-header-web',
            headerName: t('historyPage.type'),
            sortable: false,
            width: 100,
            renderCell: (params: HistoryGridRowInterface) => (
                <div
                    className='c-cell c-cell-type'
                >
                     <span>{defaultResults ? '' : (params.row?.type === "NTP" ? getDisplaySubType(params.row?.subType) : t('compensationTypes.wifi'))}</span>
                </div>
            )
        },
        {
            field: 'edit',
            headerAlign: 'center' as GridAlignment,
            headerClassName: 'col-header-web',
            headerName: t('historyPage.edit'),
            sortable: false,
            width: 100,
            renderCell: (params) => {
                const status = (params.row.status ?? params.row.voucherStatusCode) as string | undefined;
                const isPending = typeof status === 'string' && ['VALIDATING', 'QUEUED', 'PROCESSING'].includes(status.toUpperCase());
                
                // Hide edit button only for default results or pending state
                if (defaultResults || isPending) {
                    return null;
                }
                
                // For errored records, only show if there's a reasonId
                if (params.row?.error) {
                    if (!params.row?.reasonId) {
                        return (
                            <div className='c-cell-edit' style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                                <EditIcon />
                            </div>
                        );
                    }
                    return (
                        <div 
                            className='c-cell-edit' 
                            onClick={() => editErroredVoucher(params.row)}
                        >
                            <EditIcon />
                        </div>
                    );
                }
                
                // For successful records, always show the edit button
                return (
                    <div
                        className='c-cell-edit'
                        onClick={() => updateEmailAddress(params.row)}
                    >
                        <EditIcon />
                    </div>
                );
            }
        },
        ...(historyView === HISTORY_VIEW.ifs ? [{
            field: 'view',
            headerAlign: 'center' as GridAlignment,
            headerClassName: 'col-header',
            headerName: t('historyPage.viewCode'),
            sortable: false,
            width: 150,
            renderCell: (params: HistoryGridRowInterface) => {
                const status = (params.row.status ?? params.row.voucherStatusCode) as string | undefined;
                const isPending = typeof status === 'string' && ['VALIDATING', 'QUEUED', 'PROCESSING'].includes(status.toUpperCase());
                return (
                    params.row.error || isPending ? (
                        null
                    ) : (
                        <div
                            className='c-icon'
                            onClick={() => params.row.type === 'WIFI' && handleViewCode(params.row)}
                        >
                            {params.row.type === 'WIFI' ? (viewCodeLoadingRowId === params.row.id && historyContext.loading.voucherCode ? <CircularProgress size={20} /> : <PreviewIcon />) : null}
                        </div>
                    )
                );
            }
        }] : []),
        {
            field: 'resend',
            headerAlign: 'center' as GridAlignment,
            headerClassName: 'col-header-web',
            headerName: t('historyPage.send'),
            sortable: false,
            width: 100,
            renderCell: (params) => {
                const status = (params.row.status ?? params.row.voucherStatusCode) as string | undefined;
                const isPending = typeof status === 'string' && ['VALIDATING', 'QUEUED', 'PROCESSING'].includes(status.toUpperCase());
                return (
                    <div
                        className='c-cell-resend'
                        onClick={() => handleResend(params.row)}
                        style={{visibility: defaultResults || params.row?.error || isPending ? 'hidden' : 'visible'}}
                    >
                        {resendLoadingRowId === params.row.id ? <CircularProgress size={20} /> : <SendIcon />}
                    </div>
                );
            }
        }
    ];

    const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
        return (
            <MuiAlert
                elevation={6}
                ref={ref}
                variant="filled"
                {...props} 
            />
        );
    });

    return (
        <div className='c-comp-hist-rows'>
            {openUpdateEmailDialog && (
                <UpdateEmailPopUp 
                    updateEmailPayload={updateEmailPayload}
                    showPopUp={openUpdateEmailDialog} 
                    hidePopUp={() => setOpenUpdateEmailDialog(false)}
                    setUpdateEmailPayload={setUpdateEmailPayload} 
                    updateEmail={() => saveUpdatedEmailAddress(updateEmailPayload)}                    
                />
            )}
            <Snackbar
                    autoHideDuration={6000}
                    open={historyContext.emailUpdated || openSentConfirmation}
                    onClose={handleCloseEmailUpdatedConfirmation}
                >
                <Alert
                    onClose={handleCloseEmailUpdatedConfirmation}
                    severity="success"
                    sx={{ width: '100%' }}
                >
                    <div className="label">{openSentConfirmation ? t('labels.compensationResent') : t('labels.emailAddressUpdated')}</div>                    
                </Alert>
            </Snackbar>
            {rows && rows.length ? (
                <>
                    <div className="c-table-web-view">
                        <AppTable
                            columns={historyColumnsWeb}
                            pageSize={historyView === HISTORY_VIEW.nonIfs ? historyContext.historyNonIfsPageState.size : 10}
                            rows={rows}
                            pageNumber={historyView === HISTORY_VIEW.nonIfs ? historyContext.historyNonIfsPageState.number : undefined}
                            serverSidePagination={historyView === HISTORY_VIEW.nonIfs}
                            serverSidePaginationCount={historyView === HISTORY_VIEW.nonIfs ? getServerSidePaginationCount() : undefined}
                            getRowClassName={getRowClassName}
                            handleOnClickSearch={handleOnClickSearch}
                            rowsPerPageLabel={t('labels.rowsPerPage')}
                        />
                    </div>
                    <div className="c-table-ipad-view">
                        <AppTable 
                            columns={historyColumnsIpad} 
                            pageSize={historyView === HISTORY_VIEW.nonIfs ? historyContext.historyNonIfsPageState.size : 10}
                            rows={rows}
                            pageNumber={historyContext.historyNonIfsPageState.number}
                            serverSidePagination={historyView === HISTORY_VIEW.nonIfs}
                            serverSidePaginationCount={historyView === HISTORY_VIEW.nonIfs ? getServerSidePaginationCount() : undefined}
                            getRowClassName={getRowClassName}
                            handleOnClickSearch={handleOnClickSearch}
                            rowsPerPageLabel={t('labels.rowsPerPage')}
                        />
                    </div>
                </>
            ) : null}
        </div>
    );
};

export default CompensationHistoryGridResults;

